import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronLeft, ChevronRight, Play, Square, Eye, EyeOff,
  Users, Trophy, BarChart2, Copy, Loader2, X, RefreshCw, Clock, Timer,
  MessageSquare, Pin, Check, Link2, Volume2, VolumeX
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#E53E6B', '#FF9F43', '#00D2D3', '#5F27CD', '#10AC84', '#EE5A24', '#0ABDE3', '#F368E0'];
const SHAPES = ['triangle', 'diamond', 'circle', 'square'];

// ═══ SOUND EFFECTS (Web Audio API) ═══
const audioCtxRef = { current: null };
function getAudioCtx() {
  if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
  return audioCtxRef.current;
}

function playTone(freq, duration = 0.1, vol = 0.25, type = 'sine') {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(vol, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
    osc.start();
    osc.stop(ctx.currentTime + duration);
  } catch (e) { /* silent */ }
}

function playQuestionReveal() {
  // Rising chime — whoosh effect
  playTone(400, 0.08, 0.2, 'sine');
  setTimeout(() => playTone(600, 0.08, 0.25, 'sine'), 70);
  setTimeout(() => playTone(800, 0.12, 0.3, 'triangle'), 140);
  setTimeout(() => playTone(1200, 0.15, 0.2, 'sine'), 210);
}

function playResultsReveal() {
  // Dramatic reveal — descending then ascending
  playTone(300, 0.1, 0.2, 'sawtooth');
  setTimeout(() => playTone(250, 0.1, 0.2, 'sawtooth'), 100);
  setTimeout(() => playTone(500, 0.12, 0.25, 'triangle'), 250);
  setTimeout(() => playTone(700, 0.12, 0.25, 'triangle'), 350);
  setTimeout(() => playTone(900, 0.2, 0.3, 'sine'), 450);
}

function playCountdownTick() {
  playTone(880, 0.06, 0.15, 'sine');
}

function playTimeUp() {
  playTone(200, 0.25, 0.2, 'square');
  setTimeout(() => playTone(150, 0.35, 0.2, 'square'), 250);
}

// Ambient lobby music generator
function createAmbientLoop(ctx) {
  // Soft ambient pad using oscillators
  const now = ctx.currentTime;
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.06, now);
  masterGain.connect(ctx.destination);

  const notes = [261.6, 329.6, 392.0, 523.3]; // C4, E4, G4, C5
  const oscillators = notes.map(freq => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0.3, now);
    osc.connect(g);
    g.connect(masterGain);
    osc.start();
    return { osc, gain: g };
  });

  // Gentle LFO for movement
  const lfo = ctx.createOscillator();
  const lfoGain = ctx.createGain();
  lfo.frequency.value = 0.2;
  lfoGain.gain.value = 15;
  lfo.connect(lfoGain);
  oscillators.forEach(o => lfoGain.connect(o.osc.frequency));
  lfo.start();

  return {
    stop: () => {
      masterGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.5);
      setTimeout(() => {
        oscillators.forEach(o => { try { o.osc.stop(); } catch (e) { /* */ } });
        try { lfo.stop(); } catch (e) { /* */ }
      }, 1600);
    },
    setVolume: (v) => { masterGain.gain.setValueAtTime(v * 0.06, ctx.currentTime); }
  };
}

/**
 * Shared QuizPresenter component — works for both logged-in owners (quizId prop)
 * and co-presenters (quizId + presenterToken from URL).
 */
export default function QuizPresenterLive({ quizId: propQuizId, presenterToken: propToken, onClose }) {
  // Support both props and URL params
  const params = useParams();
  const [searchParams] = useSearchParams();
  const quizId = propQuizId || params.quizId;
  const presenterToken = propToken || searchParams.get('token') || '';
  const isTokenMode = !!presenterToken;

  const [quiz, setQuiz] = useState(null);
  const [status, setStatus] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [teamLeaderboard, setTeamLeaderboard] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showQA, setShowQA] = useState(false);
  const [qaQuestions, setQaQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);
  const [questionAnim, setQuestionAnim] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const lastQIdx = useRef(-1);
  const lastTickRef = useRef(null);
  const ambientRef = useRef(null);
  const wasShowingResults = useRef(false);

  useEffect(() => {
    loadQuiz();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (ambientRef.current) { ambientRef.current.stop(); ambientRef.current = null; }
    };
  }, [quizId]);

  // Ambient lobby music
  useEffect(() => {
    const isWaiting = !status?.status || status?.status === 'draft';
    if (isWaiting && soundEnabled && !ambientRef.current) {
      try {
        const ctx = getAudioCtx();
        ambientRef.current = createAmbientLoop(ctx);
      } catch (e) { /* user gesture required */ }
    }
    if ((!isWaiting || !soundEnabled) && ambientRef.current) {
      ambientRef.current.stop();
      ambientRef.current = null;
    }
  }, [status?.status, soundEnabled]);

  useEffect(() => {
    if (status?.status === 'live') {
      pollRef.current = setInterval(pollStatus, 1500);
      return () => clearInterval(pollRef.current);
    }
  }, [status?.status]);

  // Animate + sound on question change
  useEffect(() => {
    const idx = status?.current_question_index ?? -1;
    if (idx !== lastQIdx.current && idx >= 0) {
      setQuestionAnim('animate-slideIn');
      if (soundEnabled) playQuestionReveal();
      const t = setTimeout(() => setQuestionAnim(''), 600);
      lastQIdx.current = idx;
      return () => clearTimeout(t);
    }
  }, [status?.current_question_index]);

  // Sound on results reveal
  useEffect(() => {
    const showing = status?.show_results || false;
    if (showing && !wasShowingResults.current && soundEnabled) {
      playResultsReveal();
    }
    wasShowingResults.current = showing;
  }, [status?.show_results]);

  // Timer countdown
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    const timeLimit = status?.current_question?.time_limit || status?.default_time_limit || 0;
    const startedAt = status?.question_started_at;
    if (!timeLimit || !startedAt || status?.status !== 'live' || status?.show_results) {
      setTimeLeft(null);
      return;
    }
    const calc = () => {
      const elapsed = (Date.now() - new Date(startedAt).getTime()) / 1000;
      const remaining = Math.max(0, Math.ceil(timeLimit - elapsed));
      setTimeLeft(remaining);
      // Tick sound at ≤5 seconds
      if (soundEnabled && remaining > 0 && remaining <= 5 && remaining !== lastTickRef.current) {
        lastTickRef.current = remaining;
        playCountdownTick();
      }
      if (remaining <= 0 && timerRef.current) {
        clearInterval(timerRef.current);
        if (soundEnabled) playTimeUp();
      }
    };
    calc();
    timerRef.current = setInterval(calc, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [status?.current_question_index, status?.question_started_at, status?.show_results]);

  const loadQuiz = async () => {
    setLoading(true);
    try {
      if (isTokenMode) {
        const res = await axios.get(`${API}/quiz/${quizId}/present?token=${presenterToken}`);
        setQuiz(res.data);
      } else {
        const res = await axios.get(`${API}/quiz/${quizId}`);
        setQuiz(res.data);
      }
      await pollStatus();
    } catch (err) {
      alert('Failed to load quiz. Check the link.');
    } finally {
      setLoading(false);
    }
  };

  const pollStatus = async () => {
    try {
      if (isTokenMode) {
        const res = await axios.get(`${API}/quiz/${quizId}/status/presenter?token=${presenterToken}`);
        setStatus(res.data);
      } else {
        const res = await axios.get(`${API}/quiz/${quizId}/status/full`);
        setStatus(res.data);
      }
    } catch (err) {
      console.error('Poll error:', err);
    }
  };

  const controlAction = async (action) => {
    try {
      if (isTokenMode) {
        await axios.post(`${API}/quiz/${quizId}/control`, { presenter_token: presenterToken, action });
      } else {
        if (action === 'start') await axios.post(`${API}/quiz/${quizId}/start`);
        else if (action === 'next') await axios.post(`${API}/quiz/${quizId}/next`);
        else if (action === 'previous') await axios.post(`${API}/quiz/${quizId}/previous`);
        else if (action === 'show-results') await axios.post(`${API}/quiz/${quizId}/show-results`);
        else if (action === 'end') await axios.post(`${API}/quiz/${quizId}/end`);
      }
      await pollStatus();
    } catch (err) {
      alert(err.response?.data?.detail || `Failed: ${action}`);
    }
  };

  const loadLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/quiz/${quizId}/results`);
      setLeaderboard(res.data.leaderboard || []);
      setTeamLeaderboard(res.data.team_leaderboard || []);
    } catch (err) { console.error(err); }
  };

  const loadQA = async () => {
    try {
      const res = await axios.get(`${API}/quiz/${quizId}/qa`);
      setQaQuestions(res.data.questions || []);
    } catch (err) { console.error(err); }
  };

  const pinQA = async (qaId) => {
    try { await axios.post(`${API}/quiz/${quizId}/qa/${qaId}/pin`); loadQA(); } catch (err) { console.error(err); }
  };
  const markQAAnswered = async (qaId) => {
    try { await axios.post(`${API}/quiz/${quizId}/qa/${qaId}/mark-answered`); loadQA(); } catch (err) { console.error(err); }
  };

  const copyJoinUrl = () => {
    const url = `${window.location.origin}/quiz/join/${status?.join_code || quiz?.join_code}`;
    navigator.clipboard.writeText(url);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0F0C29 0%, #302B63 50%, #24243E 100%)' }}>
        <Loader2 className="w-10 h-10 animate-spin text-white/80" />
      </div>
    );
  }

  const currentQ = status?.current_question;
  const isLive = status?.status === 'live';
  const isEnded = status?.status === 'ended';
  const chartData = status?.answer_distribution?.answers
    ? Object.entries(status.answer_distribution.answers).map(([name, value]) => ({ name, value }))
    : [];
  const totalQ = status?.total_questions || 0;
  const currentIdx = status?.current_question_index ?? -1;
  const progressPct = totalQ > 0 ? ((currentIdx + 1) / totalQ) * 100 : 0;
  const timeLimit = currentQ?.time_limit || status?.default_time_limit || 0;
  const timerPct = timeLimit > 0 && timeLeft !== null ? (timeLeft / timeLimit) * 100 : 100;

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-hidden" data-testid="quiz-presenter" style={{ background: 'linear-gradient(135deg, #0F0C29 0%, #302B63 50%, #24243E 100%)' }}>
      {/* Animated Background Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="absolute rounded-full opacity-10 animate-float" style={{
            width: `${20 + Math.random() * 60}px`,
            height: `${20 + Math.random() * 60}px`,
            background: COLORS[i % COLORS.length],
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${i * 0.7}s`,
            animationDuration: `${6 + Math.random() * 8}s`,
          }} />
        ))}
      </div>

      {/* Top Bar */}
      <div className="relative flex items-center justify-between px-4 sm:px-6 py-2.5 bg-black/20 backdrop-blur-md border-b border-white/5 z-10">
        <div className="flex items-center gap-3 min-w-0">
          {onClose && (
            <button onClick={onClose} className="text-white/50 hover:text-white transition-colors" data-testid="presenter-close">
              <X className="w-5 h-5" />
            </button>
          )}
          <h2 className="text-white font-bold text-sm sm:text-lg truncate">{quiz?.title}</h2>
          <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isLive ? 'bg-emerald-500/90 text-white shadow-[0_0_12px_rgba(16,185,129,0.4)]' : isEnded ? 'bg-rose-500/90 text-white' : 'bg-white/10 text-white/70'}`}>
            {isLive ? 'LIVE' : status?.status || 'draft'}
          </span>
          {isTokenMode && (
            <span className="px-2 py-0.5 rounded-full text-[9px] bg-violet-500/20 text-violet-300 font-medium">Co-Presenter</span>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <button onClick={copyJoinUrl} className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-all" data-testid="copy-join-link">
            <Copy className="w-3.5 h-3.5" />
            <span className="font-mono font-bold text-emerald-300">{status?.join_code || quiz?.join_code}</span>
          </button>
          <button onClick={copyJoinUrl} className="sm:hidden p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors" title="Copy code">
            <Copy className="w-4 h-4" />
          </button>
          <span className="flex items-center gap-1.5 text-white/60 text-xs sm:text-sm bg-white/5 px-2.5 py-1 rounded-lg">
            <Users className="w-3.5 h-3.5" /> <span className="font-bold text-white">{status?.participant_count || 0}</span>
          </span>
          <button onClick={() => { loadLeaderboard(); setShowLeaderboard(!showLeaderboard); setShowQA(false); }} className={`p-2 rounded-lg transition-all ${showLeaderboard ? 'bg-amber-500/20 text-amber-300' : 'bg-white/10 text-white hover:bg-white/20'}`} title="Leaderboard" data-testid="toggle-leaderboard">
            <Trophy className="w-4 h-4" />
          </button>
          <button onClick={() => { loadQA(); setShowQA(!showQA); setShowLeaderboard(false); }} className={`p-2 rounded-lg transition-all ${showQA ? 'bg-sky-500/20 text-sky-300' : 'bg-white/10 text-white hover:bg-white/20'}`} title="Q&A" data-testid="toggle-qa">
            <MessageSquare className="w-4 h-4" />
          </button>
          <button onClick={() => setSoundEnabled(!soundEnabled)} className={`p-2 rounded-lg transition-all ${soundEnabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-white/40 hover:bg-white/20'}`} title={soundEnabled ? 'Mute sounds' : 'Unmute sounds'} data-testid="toggle-sound">
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      {isLive && (
        <div className="relative h-1 bg-white/5 z-10">
          <div className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 transition-all duration-500 ease-out" style={{ width: `${progressPct}%` }} />
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 relative z-10">
        {/* WAITING TO START */}
        {!isLive && !isEnded && (
          <div className="text-center px-4 animate-fadeIn">
            <div className="mb-8">
              <p className="text-white/40 text-sm mb-3 uppercase tracking-widest font-medium">Join Code</p>
              <p className="text-5xl sm:text-7xl font-black text-white font-mono tracking-[0.3em] drop-shadow-[0_0_30px_rgba(255,255,255,0.15)]">{quiz?.join_code}</p>
              <p className="text-white/40 text-sm mt-6 flex items-center justify-center gap-2">
                <Users className="w-4 h-4" /> {status?.participant_count || 0} participant(s) waiting
              </p>
            </div>
            <button onClick={() => controlAction('start')} className="px-8 py-4 bg-emerald-500 hover:bg-emerald-400 text-white rounded-2xl text-lg font-bold flex items-center gap-3 mx-auto transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(16,185,129,0.4)]" data-testid="start-quiz-btn">
              <Play className="w-6 h-6" /> Start Quiz
            </button>
          </div>
        )}

        {/* LIVE QUESTION */}
        {isLive && currentQ && !showLeaderboard && !showQA && (
          <div className={`w-full max-w-5xl px-2 sm:px-4 ${questionAnim}`}>
            {/* Question Header */}
            <div className="text-center mb-6 sm:mb-10">
              <p className="text-white/30 text-xs sm:text-sm mb-3 font-medium tracking-wider">
                QUESTION {currentIdx + 1} <span className="text-white/15">/ {totalQ}</span>
              </p>
              <h3 className="text-2xl sm:text-4xl font-black text-white leading-snug max-w-3xl mx-auto drop-shadow-lg">{currentQ.text}</h3>

              {/* Timer Bar */}
              {timeLeft !== null && timeLeft > 0 && (
                <div className="mt-5 max-w-md mx-auto" data-testid="presenter-timer">
                  <div className="flex items-center gap-3 justify-center mb-2">
                    <Timer className={`w-5 h-5 ${timeLeft <= 5 ? 'text-rose-400 animate-pulse' : 'text-white/50'}`} />
                    <span className={`text-3xl font-mono font-black tabular-nums ${timeLeft <= 5 ? 'text-rose-400' : timeLeft <= 10 ? 'text-amber-400' : 'text-white/80'}`}>
                      {timeLeft}
                    </span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-1000 ease-linear ${timerPct <= 20 ? 'bg-rose-500' : timerPct <= 50 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${timerPct}%` }} />
                  </div>
                </div>
              )}
              {timeLeft === 0 && (
                <div className="mt-4 inline-flex items-center gap-2 text-rose-400 animate-pulse" data-testid="time-up-indicator">
                  <Clock className="w-5 h-5" />
                  <span className="text-xl font-bold">Time's up!</span>
                </div>
              )}
            </div>

            {/* Answer Results or Options */}
            {(status?.show_results || (currentQ.show_results_live && chartData.length > 0)) && chartData.length > 0 ? (
              <div className="bg-white/5 rounded-3xl p-6 sm:p-8 backdrop-blur-sm border border-white/10 animate-fadeIn">
                {currentQ.type === 'emoji_reaction' ? (
                  <div className="flex flex-wrap gap-6 justify-center items-end min-h-[150px]">
                    {chartData.map((item, i) => (
                      <div key={i} className="flex flex-col items-center gap-2 animate-popIn" style={{ animationDelay: `${i * 0.1}s` }}>
                        <span className="text-white/70 font-bold text-lg">{item.value}</span>
                        <div className="px-4 py-3 rounded-2xl bg-white/10" style={{ transform: `scale(${Math.min(1.5, 0.8 + item.value * 0.15)})` }}>
                          <span className="text-4xl">{item.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : currentQ.type === 'rating' ? (
                  <div className="text-center">
                    <div className="text-7xl font-black text-amber-400 mb-2">{status.answer_distribution?.average_rating || '—'}</div>
                    <div className="flex justify-center gap-1 mb-2">
                      {[1,2,3,4,5].map(s => (
                        <span key={s} className={`text-4xl ${s <= Math.round(status.answer_distribution?.average_rating || 0) ? 'text-amber-400' : 'text-white/10'}`}>★</span>
                      ))}
                    </div>
                    <p className="text-white/40 text-sm">{status.answer_distribution?.rating_count || 0} ratings</p>
                  </div>
                ) : (currentQ.type === 'word_cloud' || currentQ.type === 'open_ended') ? (
                  <div className="flex flex-wrap gap-3 justify-center items-center min-h-[200px]">
                    {chartData.map((item, i) => (
                      <span key={i} className="px-5 py-2 rounded-full text-white font-bold transition-all animate-popIn" style={{
                        fontSize: `${Math.min(2.5, 0.9 + item.value * 0.4)}rem`,
                        backgroundColor: COLORS[i % COLORS.length],
                        animationDelay: `${i * 0.05}s`,
                      }}>
                        {item.name} {item.value > 1 && <span className="text-xs opacity-70 ml-1">×{item.value}</span>}
                      </span>
                    ))}
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} layout="vertical" margin={{ left: 30, right: 30 }}>
                      <XAxis type="number" stroke="#ffffff30" tick={{ fill: '#ffffff80', fontSize: 13 }} />
                      <YAxis type="category" dataKey="name" stroke="#ffffff40" width={140} tick={{ fill: '#ffffffCC', fontSize: 14, fontWeight: 600 }} />
                      <Tooltip contentStyle={{ background: '#1a1a3e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: 'white', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }} />
                      <Bar dataKey="value" radius={[0, 12, 12, 0]}>
                        {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
                <p className="text-center text-white/30 text-sm mt-4 font-medium">
                  {status.answer_distribution?.total_responses || 0} responses
                  {status.answer_distribution?.first_correct && (
                    <span className="ml-3 text-amber-300/80">
                      Fastest: <span className="font-bold text-amber-300">{status.answer_distribution.first_correct}</span>
                    </span>
                  )}
                </p>
                {currentQ.correct_answer && !['word_cloud','open_ended','poll','poll_multi','emoji_reaction','rating'].includes(currentQ.type) && (
                  <p className="text-center text-emerald-400 font-bold mt-3 text-lg">
                    Correct: {currentQ.correct_answer}
                  </p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentQ.image_url && (
                  <div className="md:col-span-2 flex justify-center mb-2">
                    <img src={currentQ.image_url} alt="" className="rounded-2xl max-h-64 object-contain shadow-2xl" />
                  </div>
                )}
                {(currentQ.options || []).map((opt, i) => (
                  <div key={i} className="group p-5 sm:p-6 rounded-2xl text-white text-lg sm:text-xl font-bold flex items-center gap-4 transition-all hover:scale-[1.02] animate-popIn" style={{
                    backgroundColor: COLORS[i % COLORS.length],
                    animationDelay: `${i * 0.1}s`,
                    boxShadow: `0 8px 24px ${COLORS[i % COLORS.length]}50`,
                  }} data-testid={`option-${i}`}>
                    {currentQ.type === 'emoji_reaction' ? (
                      <span className="text-4xl">{opt}</span>
                    ) : (
                      <>
                        <span className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-base font-black flex-shrink-0">
                          {String.fromCharCode(65 + i)}
                        </span>
                        <span className="leading-snug">{opt}</span>
                      </>
                    )}
                  </div>
                ))}
                {currentQ.type === 'rating' && (
                  <div className="md:col-span-2 flex justify-center gap-4 py-4">
                    {[1,2,3,4,5].map(s => <span key={s} className="text-5xl text-white/20">★</span>)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ENDED */}
        {isEnded && !showLeaderboard && (
          <div className="text-center animate-fadeIn">
            <Trophy className="w-20 h-20 text-amber-400 mx-auto mb-4 drop-shadow-[0_0_20px_rgba(251,191,36,0.3)]" />
            <h3 className="text-4xl font-black text-white mb-2">Quiz Complete!</h3>
            <p className="text-white/50 mb-8">{status?.participant_count || 0} participants</p>
            <button onClick={() => { loadLeaderboard(); setShowLeaderboard(true); }} className="px-8 py-3 bg-amber-500 hover:bg-amber-400 text-white rounded-2xl font-bold transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(251,191,36,0.4)]">
              View Leaderboard
            </button>
          </div>
        )}

        {/* LEADERBOARD */}
        {showLeaderboard && (
          <div className="w-full max-w-2xl animate-fadeIn">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-black text-white flex items-center gap-3"><Trophy className="w-7 h-7 text-amber-400" /> Leaderboard</h3>
              <button onClick={() => setShowLeaderboard(false)} className="text-white/30 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
            </div>
            {status?.team_mode && teamLeaderboard.length > 0 && (
              <div className="mb-6">
                <h4 className="text-xs font-bold text-white/40 uppercase tracking-widest mb-3">Team Standings</h4>
                <div className="space-y-2">
                  {teamLeaderboard.map((team, i) => (
                    <div key={team.id} className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm" style={{ borderLeftColor: team.color, borderLeftWidth: '4px' }}>
                      <span className="w-9 h-9 rounded-full flex items-center justify-center font-black text-sm" style={{ backgroundColor: team.color + '40', color: team.color }}>{i + 1}</span>
                      <span className="flex-1 text-white font-bold">{team.name}</span>
                      <span className="text-white/40 text-sm">{team.members} members</span>
                      <span className="font-black text-lg" style={{ color: team.color }}>{team.score} pts</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              {status?.team_mode && <h4 className="text-xs font-bold text-white/40 uppercase tracking-widest mb-3">Individual Rankings</h4>}
              <div className="space-y-2">
                {leaderboard.map((p, i) => (
                  <div key={p.id} className={`flex items-center gap-4 p-4 rounded-2xl backdrop-blur-sm transition-all ${i === 0 ? 'bg-amber-500/20 border border-amber-500/30 shadow-[0_0_20px_rgba(251,191,36,0.1)]' : i === 1 ? 'bg-white/8 border border-white/10' : i === 2 ? 'bg-white/5 border border-white/5' : 'bg-white/[0.02]'}`}>
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center font-black text-sm ${i === 0 ? 'bg-amber-400 text-amber-900' : i === 1 ? 'bg-white/20 text-white' : 'bg-white/10 text-white/50'}`}>{i + 1}</span>
                    <span className="flex-1 text-white font-bold">{p.name}</span>
                    <span className="text-white/40 text-sm">{p.responses_count} answered</span>
                    <span className={`font-black text-lg ${i === 0 ? 'text-amber-400' : 'text-white/70'}`}>{p.score} pts</span>
                  </div>
                ))}
                {leaderboard.length === 0 && <p className="text-white/30 text-center py-8">No participants yet</p>}
              </div>
            </div>
          </div>
        )}

        {/* Q&A Panel */}
        {showQA && (
          <div className="w-full max-w-lg animate-fadeIn">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-black text-white flex items-center gap-3"><MessageSquare className="w-6 h-6 text-sky-400" /> Audience Q&A</h3>
              <div className="flex items-center gap-2">
                <button onClick={loadQA} className="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white"><RefreshCw className="w-4 h-4" /></button>
                <button onClick={() => setShowQA(false)} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {qaQuestions.map(q => (
                <div key={q.id} className={`p-4 rounded-2xl backdrop-blur-sm ${q.pinned ? 'bg-sky-500/15 border border-sky-500/30' : 'bg-white/5 border border-white/5'} ${q.answered ? 'opacity-40' : ''}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <p className="text-white font-medium">{q.text}</p>
                      <p className="text-xs text-white/30 mt-1">{q.submitted_by} · {q.votes} votes</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => pinQA(q.id)} className={`p-1.5 rounded-lg ${q.pinned ? 'bg-sky-500 text-white' : 'bg-white/10 text-white/50 hover:text-white'}`}>
                        <Pin className="w-3.5 h-3.5" />
                      </button>
                      {!q.answered && (
                        <button onClick={() => markQAAnswered(q.id)} className="p-1.5 bg-white/10 text-white/50 hover:text-emerald-400 rounded-lg">
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {qaQuestions.length === 0 && <p className="text-white/30 text-center py-8">No questions yet.</p>}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Controls */}
      {isLive && (
        <div className="relative flex items-center justify-between px-4 sm:px-6 py-3 bg-black/30 backdrop-blur-md border-t border-white/5 z-10">
          <button onClick={() => controlAction('previous')} disabled={currentIdx === 0} className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center gap-2 disabled:opacity-20 transition-all text-sm font-medium" data-testid="prev-question-btn">
            <ChevronLeft className="w-5 h-5" /> <span className="hidden sm:inline">Previous</span>
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={() => controlAction('show-results')} className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all text-sm font-medium ${status?.show_results ? 'bg-violet-500/30 text-violet-300 border border-violet-500/30' : 'bg-white/10 hover:bg-white/20 text-white'}`} data-testid="toggle-results-btn">
              {status?.show_results ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span className="hidden sm:inline">{status?.show_results ? 'Hide' : 'Results'}</span>
            </button>
            <button onClick={() => { if (window.confirm('End this quiz session?')) controlAction('end'); }} className="px-4 py-2.5 bg-rose-500/80 hover:bg-rose-500 text-white rounded-xl flex items-center gap-2 transition-all text-sm font-medium" data-testid="end-quiz-btn">
              <Square className="w-4 h-4" /> <span className="hidden sm:inline">End</span>
            </button>
          </div>
          <button onClick={() => controlAction('next')} disabled={currentIdx + 1 >= totalQ} className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center gap-2 disabled:opacity-20 transition-all text-sm font-medium" data-testid="next-question-btn">
            <span className="hidden sm:inline">Next</span> <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes slideIn { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slideIn { animation: slideIn 0.5s ease-out; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out; }
        @keyframes popIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
        .animate-popIn { animation: popIn 0.4s ease-out both; }
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-30px) scale(1.1); }
        }
        .animate-float { animation: float 8s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
